import { CLASSES, ARIAS, MINICART } from '../utils/constants';
import renderMinicart from '../lib/renderMinicart';
import { lockBodyScroll, unlockBodyScroll } from '../utils/bodyScrollLock';

const MINICART_OPEN_CLASS = 'minicart-open';
const MINICART_TRANSITION_MS = 300;
const FOCUS_FALLBACK_BUFFER_MS = 50;
// Announce once focus has settled in the dialog, so the focus/dialog-open
// announcement doesn't swallow the status message in the same speech burst.
const ANNOUNCE_DELAY_MS = 350;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

const initMinicart = () => {
  const minicartToggle = document.querySelector('[data-minicart-toggle]');
  const minicart = document.querySelector('[data-minicart]');
  const minicartCloseBtn = minicart?.querySelector('[data-minicart-close]');
  const minicartOverlay = document.querySelector('[data-minicart-overlay]');

  if (!minicartToggle || !minicart) return;

  let previouslyFocusedEl = null;
  let focusTimeout = null;
  let announceTimeout = null;
  let pendingAnnouncement = '';
  const minicartStatus = minicart.querySelector('[data-minicart-status]');
  const cartCountEl = document.querySelector('[data-cart-count]');

  const getCartItemCount = () => {
    const count = parseInt(cartCountEl?.textContent.trim(), 10);
    return Number.isNaN(count) ? 0 : count;
  };

  const buildMinicartLabel = () => {
    const title = minicart.dataset.minicartTitle || '';
    const count = getCartItemCount();
    const countTemplate = count === 1 ? minicart.dataset.minicartCountOne : minicart.dataset.minicartCountOther;
    const countLabel = countTemplate ? countTemplate.replace('__COUNT__', count) : '';
    return [title, countLabel].filter(Boolean).join(', ');
  };

  const announce = (message) => {
    if (!minicartStatus || !message) return;
    // Clear then set on the next frame so the assertive region always sees a
    // content change (empty -> message) and re-announces, even for repeat adds
    // of the same item.
    window.clearTimeout(announceTimeout);
    minicartStatus.textContent = '';
    announceTimeout = window.setTimeout(() => {
      minicartStatus.textContent = message;
    }, ANNOUNCE_DELAY_MS);
  };

  const focusMinicartDialog = () => {
    // Move focus to the close button (the dialog's first interactive control)
    // so it can be activated immediately with Enter/Space. The dialog is still
    // announced to screen readers via role="dialog"/aria-modal/aria-label;
    // fall back to the container if the button is missing.
    if (minicartCloseBtn) {
      minicartCloseBtn.focus();
    } else {
      minicart.focus();
    }

    // Announce after focus has settled, so the dialog-open announcement
    // (role + aria-label) doesn't bury the status message.
    if (pendingAnnouncement) {
      announce(pendingAnnouncement);
      pendingAnnouncement = '';
    }
  };

  const focusMinicartAfterTransition = () => {
    window.clearTimeout(focusTimeout);

    const handleTransitionEnd = (evt) => {
      if (evt.target !== minicart || evt.propertyName !== 'transform') return;

      minicart.removeEventListener('transitionend', handleTransitionEnd);
      window.clearTimeout(focusTimeout);
      focusMinicartDialog();
    };

    minicart.addEventListener('transitionend', handleTransitionEnd);
    focusTimeout = window.setTimeout(() => {
      minicart.removeEventListener('transitionend', handleTransitionEnd);
      focusMinicartDialog();
    }, MINICART_TRANSITION_MS + FOCUS_FALLBACK_BUFFER_MS);
  };

  const setMinicartState = (minicart, isOpen) => {
    minicart.classList.toggle(MINICART_OPEN_CLASS, isOpen);
    minicartToggle.setAttribute(ARIAS.EXPANDED, isOpen);
    minicart.setAttribute('aria-hidden', String(!isOpen));
    minicart.classList.toggle(CLASSES.TRANSLATE_X_0, isOpen);
    minicart.classList.toggle(CLASSES.TRANSLATE_X_100, !isOpen);

    if (isOpen) {
      lockBodyScroll(MINICART.SCROLL_LOCK_TOKEN);
    } else {
      window.clearTimeout(focusTimeout);
      window.clearTimeout(announceTimeout);
      unlockBodyScroll(MINICART.SCROLL_LOCK_TOKEN);
      pendingAnnouncement = '';
      if (minicartStatus) minicartStatus.textContent = '';
      // Move focus out of the minicart before it is hidden, so focus is never
      // trapped inside an aria-hidden, off-screen panel. Prefer the element
      // that was focused before opening; fall back to the cart toggle (which
      // always exists) when that element is gone, was inside the minicart, or
      // is not a usable focus target (e.g. <body>, or a now-disabled button).
      const canRestore =
        previouslyFocusedEl &&
        previouslyFocusedEl !== document.body &&
        !previouslyFocusedEl.disabled &&
        document.contains(previouslyFocusedEl) &&
        !minicart.contains(previouslyFocusedEl);
      const restoreTarget = canRestore ? previouslyFocusedEl : minicartToggle;
      restoreTarget?.focus();
      previouslyFocusedEl = null;
    }
  };

  const open = ({ announcement = '' } = {}) => {
    previouslyFocusedEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Name the dialog with the cart title and its current item count (e.g.
    // "Your Bag, 3 items") so screen readers announce what the user entered
    // and what it contains, rather than a raw count of focusable children.
    minicart.setAttribute('aria-label', buildMinicartLabel());
    // Defer the status message (e.g. "Added to basket") until focus settles in
    // the dialog — see focusMinicartDialog — so it isn't swallowed by the
    // dialog-open announcement.
    pendingAnnouncement = announcement;
    setMinicartState(minicart, true);
    focusMinicartAfterTransition();
  };

  const toggle = (evt) => {
    evt.preventDefault();
    const isOpen = minicart.classList.contains(MINICART_OPEN_CLASS);
    if (!isOpen) {
      previouslyFocusedEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      minicart.setAttribute('aria-label', buildMinicartLabel());
    }
    setMinicartState(minicart, !isOpen);
    if (!isOpen) focusMinicartAfterTransition();
  };

  minicartToggle.addEventListener('click', toggle);
  minicartCloseBtn?.addEventListener('click', toggle);
  minicartOverlay?.addEventListener('click', toggle);
  minicart.addEventListener('keydown', (evt) => {
    if (!minicart.classList.contains(MINICART_OPEN_CLASS)) return;

    if (evt.key === 'Escape') {
      evt.preventDefault();
      setMinicartState(minicart, false);
      return;
    }

    if (evt.key !== 'Tab') return;

    const focusableElements = [...minicart.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter(element => element.getClientRects().length);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (document.activeElement === minicart) {
      evt.preventDefault();
      (evt.shiftKey ? lastFocusableElement : firstFocusableElement)?.focus();
    } else if (evt.shiftKey && document.activeElement === firstFocusableElement) {
      evt.preventDefault();
      lastFocusableElement?.focus();
    } else if (!evt.shiftKey && document.activeElement === lastFocusableElement) {
      evt.preventDefault();
      firstFocusableElement?.focus();
    }
  });

  window.theme.minicart = { open };

  renderMinicart();
};

export default initMinicart;
